import { API_ENDPOINTS } from '../config/api';

class AIService {
  /**
   * Get AI-powered financial insights
   */
  async getInsights(userId = 'demo_user') {
    try {
      console.log('🤖 Fetching AI insights...');
      
      const response = await fetch(`${API_ENDPOINTS.AI.INSIGHTS}?userId=${userId}`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch insights');
      }
      
      console.log('✅ AI insights received:', data.insights.length);
      return data.insights;
      
    } catch (error) {
      console.error('❌ Error fetching AI insights:', error);
      
      // Return fallback insights if AI service fails
      return this.getFallbackInsights();
    }
  }

  /**
   * Send a chat message to AI assistant
   */
  async sendChatMessage(message, chatHistory = [], userId = 'demo_user') {
    try {
      console.log('💬 Sending chat message to AI...');
      
      const response = await fetch(API_ENDPOINTS.AI.CHAT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message,
          chatHistory,
          userId
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to send chat message');
      }
      
      console.log('✅ AI response received');
      return data.response;
      
    } catch (error) {
      console.error('❌ Error in AI chat:', error);
      
      // Return fallback response if AI service fails
      return {
        type: 'ai',
        content: "I'm sorry, I'm having trouble connecting right now. Please try again in a moment.",
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Check AI service status
   */
  async getStatus() {
    try {
      const response = await fetch(API_ENDPOINTS.AI.STATUS);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to check AI status');
      }
      
      return data;
      
    } catch (error) {
      console.error('❌ Error checking AI status:', error);
      return {
        success: false,
        configured: false,
        features: {
          insights: false,
          chat: false,
          transaction_analysis: false
        }
      };
    }
  }

  /**
   * Fallback insights when AI service is not available
   */
  getFallbackInsights() {
    return [
      {
        title: "Connect Your Accounts",
        message: "Your Plaid integration is working! You have real account data flowing through the system.",
        action: "Continue exploring your financial dashboard",
        priority: "medium"
      },
      {
        title: "Review Your Spending",
        message: "Check out the Transactions page to see your real spending patterns from Plaid.",
        action: "Visit the Transactions page to analyze your spending",
        priority: "medium"
      },
      {
        title: "Set Up AI Features",
        message: "Add your OpenAI API key to enable intelligent financial insights and chat assistance.",
        action: "Configure OpenAI API key in your server environment",
        priority: "high"
      }
    ];
  }

  /**
   * Format insights for display
   */
  formatInsightForDisplay(insight) {
    const priorityColors = {
      high: '#DC2626',
      medium: '#D97706', 
      low: '#059669'
    };

    const priorityIcons = {
      high: '🚨',
      medium: '⚠️',
      low: 'ℹ️'
    };

    return {
      ...insight,
      color: priorityColors[insight.priority] || priorityColors.medium,
      icon: priorityIcons[insight.priority] || priorityIcons.medium
    };
  }

  /**
   * Generate a unique message ID for chat
   */
  generateMessageId() {
    return `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Format chat message for storage
   */
  formatChatMessage(content, type = 'user') {
    return {
      id: this.generateMessageId(),
      type,
      content,
      timestamp: new Date().toISOString()
    };
  }
}

const aiServiceInstance = new AIService();
export default aiServiceInstance; 